import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
import {
  COUNT_FIGURE,
  COUNT_FIGURES,
  COUNT_HEAD,
  COUNTS_GRID,
  ZONE_CARD,
  countGroup,
  tileCell,
  tileGrid,
} from '@/screens/signals/lib/signal-zone-boxes'

/*
 * The dashboard's loading shapes, cut from the loaded blocks' own boxes
 * (`lib/signal-zone-boxes.ts`) so the pending page and the page are the same
 * height block for block. A light module (Skeleton, `cn` and the boxes): the
 * route's `pendingComponent` ships in the entry chunk. Bars only, no words.
 */

/** A bar centred in a text line box of `line` height (the `Line` idiom of SignalsSkeletons). */
function Line({ line, bar }: { line: string; bar: string }) {
  return (
    <div className={cn('flex items-center', line)}>
      <Skeleton className={bar} />
    </div>
  )
}

/**
 * A counts-panel group: the 14/600 title and its total on the 20px head line,
 * `figures` figures (the 20/28 number and its words), and the split bar's 12px box.
 */
function CountGroupSkeleton({ index, figures }: { index: number; figures: number }) {
  return (
    <div className={countGroup(index)}>
      <div className={COUNT_HEAD}>
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-3 w-16" />
      </div>
      <div className={COUNT_FIGURES}>
        {Array.from({ length: figures }, (_, figure) => (
          <div key={figure} className={COUNT_FIGURE}>
            <Skeleton className="h-5 w-24" />
          </div>
        ))}
      </div>
      <Line line="h-3" bar="h-2 w-full" />
    </div>
  )
}

/** `SignalSummaryCard`: the two groups (two figures, three) side by side in one panel. */
export function SummaryCardSkeleton() {
  return (
    <div className={cn(ZONE_CARD, '@container')}>
      <div className={COUNTS_GRID}>
        <CountGroupSkeleton index={0} figures={2} />
        <CountGroupSkeleton index={1} figures={3} />
      </div>
    </div>
  )
}

/**
 * The NEPSE chart's region, what `CardState` holds while the bars load: the
 * session's prices line, the 320px chart and its key.
 */
export function NepseChartSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Line line="h-4" bar="h-3 w-80 max-w-full" />
      {/* h-80: NEPSE_CHART_HEIGHT's 320px. */}
      <Skeleton className="h-80 w-full" />
      <Line line="h-4" bar="h-3 w-48" />
    </div>
  )
}

/** `SignalCandleChart`: the four 32px chips, then the chart region. */
export function CandleChartSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex flex-wrap gap-2">
        {['w-20', 'w-24', 'w-24', 'w-32'].map((width, index) => (
          <Skeleton key={index} className={cn('h-8', width)} />
        ))}
      </div>
      <div className="mt-6">
        <NepseChartSkeleton />
      </div>
    </div>
  )
}

/** `SignalMarketTiles`: the plain ruled strip, four cells of a name, a figure and a line. */
function MarketStripSkeleton() {
  return (
    <div className="@container mt-1">
      <div className={cn(tileGrid(4), 'border-y border-border-subtle')}>
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className={tileCell(index, 4, true)}>
            <Line line="h-4" bar="h-3 w-20" />
            <Line line="h-6" bar="h-4 w-24" />
            <Line line="h-4" bar="h-3 w-28" />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * `NepseSessionChart`: the title's row (the 36px button sets its height), the
 * four-cell stat strip and the candle chart's shape, 16px apart and 24 before the chart.
 */
export function NepseSectionSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex h-9 items-center justify-between gap-3">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-9 w-40" />
      </div>
      <MarketStripSkeleton />
      <CandleChartSkeleton className="mt-2" />
    </div>
  )
}
